import BigNumber from 'bignumber.js';

import { Transfer, TransferKind, TransferStatus } from '#/api/transfers';

import { shortDateFormat, timeFormat } from '#/utils/date';
import { Nullable } from '#/utils/types';

type TransferKindLabel = 'Deposit' | 'Withdrawal';
type TransferStatusLabel = 'Pending' | 'Completed' | 'Available' | 'Failed';
type TransferAction = 'withdraw-to-wallet' | 'deposit-to-paraclear';

export interface TransferView {
  readonly id: Transfer['id'];
  readonly createdTime: string;
  readonly createdDate: string;
  readonly kind: TransferKind;
  readonly kindLabel: TransferKindLabel;
  readonly status: TransferStatus;
  readonly statusLabel: TransferStatusLabel;
  readonly statusLayer: 'L1' | 'L2' | '';
  readonly amount: string;
  readonly amountRaw: BigNumber;
  readonly txHashL2: Nullable<string>;
  readonly txHashL1: Nullable<string>;
  readonly isWaitingTxHashL2: boolean;
  readonly action: Nullable<TransferAction>;
  readonly actionLabel: string;
  readonly token: string;
}

export function transferView(transfer: Transfer): TransferView {
  switch (transfer.kind) {
    case 'DEPOSIT':
      return depositView(transfer);
    case 'WITHDRAWAL':
      return withdrawalView(transfer);
    default:
    // no default
  }
  throw new Error('Invalid transfer kind');
}

function depositView(transfer: Transfer): TransferView {
  const status = DEPOSIT_STATUS_STATUS_MAP[transfer.status];
  const action = depositActionView(transfer);
  return {
    ...baseTransferView(transfer),
    statusLabel: status.statusLabel,
    statusLayer: status.statusLayer,
    action: action.action,
    actionLabel: action.actionLabel,
  };
}

function depositActionView(
  transfer: Transfer,
): Pick<TransferView, 'action' | 'actionLabel'> {
  const action =
    transfer.status === 'AVAILABLE' || transfer.status === 'FAILED'
      ? 'deposit-to-paraclear'
      : null;
  const actionLabel = action != null ? ACTION_LABEL_MAP[action] : '';
  return { action, actionLabel };
}

function withdrawalView(transfer: Transfer): TransferView {
  const status = WITHDRAWAL_STATUS_STATUS_MAP[transfer.status];
  const action = withdrawActionView(transfer);
  return {
    ...baseTransferView(transfer),
    statusLabel: status.statusLabel,
    statusLayer: status.statusLayer,
    action: action.action,
    actionLabel: action.actionLabel,
  };
}

function withdrawActionView(
  transfer: Transfer,
): Pick<TransferView, 'action' | 'actionLabel'> {
  const action = transfer.status === 'AVAILABLE' ? 'withdraw-to-wallet' : null;
  const actionLabel = action != null ? ACTION_LABEL_MAP[action] : '';
  return { action, actionLabel };
}

function baseTransferView(transfer: Transfer) {
  const txHashL2 = transfer.txn_hash === '' ? null : transfer.txn_hash;
  const txHashL1 = transfer.txn_hash_l1 === '' ? null : transfer.txn_hash_l1;
  const isWaitingTxHashL2 =
    transfer.txn_hash === '' && transfer.status === 'COMPLETED';

  return {
    id: transfer.id,
    createdTime: timeFormat(transfer.created_at),
    createdDate: shortDateFormat(transfer.created_at),
    kind: transfer.kind,
    kindLabel: KIND_LABEL_MAP[transfer.kind],
    status: transfer.status,
    amount: `${transfer.amount.toFormat(2)} USDC`,
    amountRaw: transfer.amount,
    txHashL2,
    txHashL1,
    isWaitingTxHashL2,
    token: transfer.token,
  };
}

const KIND_LABEL_MAP = {
  DEPOSIT: 'Deposit',
  WITHDRAWAL: 'Withdrawal',
} as const;

type StatusLabelMap = {
  [status in TransferStatus]: Pick<TransferView, 'statusLabel' | 'statusLayer'>;
};

const DEPOSIT_STATUS_STATUS_MAP: StatusLabelMap = {
  PENDING: { statusLabel: 'Pending', statusLayer: 'L2' },
  AVAILABLE: { statusLabel: 'Available', statusLayer: 'L2' },
  COMPLETED: { statusLabel: 'Completed', statusLayer: 'L2' },
  FAILED: { statusLabel: 'Failed', statusLayer: 'L2' },
};

const WITHDRAWAL_STATUS_STATUS_MAP: StatusLabelMap = {
  PENDING: { statusLabel: 'Pending', statusLayer: 'L2' },
  AVAILABLE: { statusLabel: 'Available', statusLayer: 'L1' },
  COMPLETED: { statusLabel: 'Completed', statusLayer: 'L1' },
  FAILED: { statusLabel: 'Failed', statusLayer: 'L2' },
};

type ActionLabelMap = {
  [action in TransferAction]: string;
};

const ACTION_LABEL_MAP: ActionLabelMap = {
  'withdraw-to-wallet': 'Withdraw to Wallet',
  'deposit-to-paraclear': 'Deposit to Paraclear',
};
